import {
  Box,
  Select,
  DateRangePicker,
  AVAILABLE_TIMEZONES,
  InterestTerm,
  Timestamp,
  Timezone,
  objectEntries,
  withStyles,
} from 'vopay';

import { useState } from 'react';
import { Button, TableFiltersContainer } from 'UI';
import styles from './SearchBar.module.scss';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import { useTranslation } from 'react-i18next';
import useFileDownload from '@/hooks/useFiledDownload';

const cn = withStyles(styles);

type TransactionSearchParams = {
  accountType?: InterestTerm;
  timeRange: [Timestamp, Timestamp];
  timezone: Timezone;
};

type InterestHistorySearchFormProps = {
  disabled: boolean;
  hasLongTermAccount: boolean;
  tableID: string;
  onSearch: (searchParams: TransactionSearchParams) => void;
};

export default function InterestHistorySearchForm({
  disabled,
  hasLongTermAccount,
  tableID,
  onSearch,
}: InterestHistorySearchFormProps) {
  const { downloadCsvFile } = useFileDownload();

  const { t } = useTranslation('translations', {
    keyPrefix: 'Accounts.InterestAccountsDrawer.InterestHistoryTab',
  });

  const { t: tDrawer } = useTranslation('translations', {
    keyPrefix: 'Accounts.InterestAccountsDrawer',
  });

  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const [timeRange, setTimeRange] = useState<[Timestamp, Timestamp]>();
  const [accountType, setAccountType] = useState<InterestTerm | undefined>();
  const { userSettings } = useUserSettingsProvider();

  const accountTypeOptions: Record<InterestTerm, string> = {
    'short-term': tDrawer('standard'),
    'long-term': tDrawer('premium'),
  };

  const accountTypeOptionsArray = objectEntries(accountTypeOptions).map(([value, label]) => ({
    value,
    label,
  }));

  const [timezone, setTimezone] = useState<Timezone>(userSettings.timezone);
  const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const initialTimeZones = AVAILABLE_TIMEZONES.map((tz) => ({
    label: tz,
    value: tz,
  })).filter((tz) => !tz.value.toLowerCase().includes('utc') && !tz.value.includes(timezoneName));

  const timeZoneOptions = [
    {
      label: 'UTC',
      value: 'UTC',
    },
    {
      label: timezoneName,
      value: timezoneName,
    },
    ...initialTimeZones,
  ];

  return (
    <TableFiltersContainer filtersApplied={!!accountType}>
      <div
        style={{
          marginLeft: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 16,
        }}
      >
        <TableFiltersContainer.Trigger />

        <Button
          size="small"
          disabled={disabled}
          onClick={() => downloadCsvFile(tableID, t('interestHistory', { defaultValue: 'Interest History' }))}
        >
          {tGlobal('ExportDropdown.label')}
        </Button>
      </div>

      <TableFiltersContainer.Content>
        <form
          onSubmit={(e) => {
            e.preventDefault();

            timeRange &&
              onSearch({
                accountType,
                timeRange,
                timezone,
              });
          }}
          className={cn('searchBar')}
        >
          <DateRangePicker
            triggerVariant="outlineShadow"
            disabled={disabled}
            onChange={(range) => {
              if (range && range[0] && range[1]) {
                setTimeRange(range);
              }
            }}
          />

          {hasLongTermAccount && (
            <Box ps="sm" width="150px">
              <Select
                version="2"
                disabled={disabled}
                placeholder={t('AccountType.placeholder', { defaultValue: 'Acc. Type' })}
                options={accountTypeOptionsArray}
                name="status"
                value={accountType}
                showClear
                onBlur={() => {}}
                onChange={(_, o) => {
                  if (o.data) {
                    setAccountType(o.data.value);
                  } else {
                    setAccountType(undefined);
                  }
                }}
              />
            </Box>
          )}

          <Select
            version="2"
            disabled={disabled}
            placeholder={t('Timezone.placeholder', { defaultValue: 'Timezone' })}
            options={timeZoneOptions}
            name="timeZone"
            value={timezone}
            onBlur={() => {}}
            filterable
            onChange={(e) => {
              setTimezone(e.target.value as Timezone);
            }}
            sort={false}
          />

          <Button
            size="small"
            disabled={disabled}
            colorScheme="primary"
            className={cn('searchButton')}
            id="search-transaction-history"
            type="submit"
          >
            {tGlobal('Buttons.search')}
          </Button>
        </form>
      </TableFiltersContainer.Content>
    </TableFiltersContainer>
  );
}
